import React, { useEffect, useState } from "react";
import { ActionIcon, Alert, Button, ComboboxItemGroup, Container, Divider, Group, Image, Indicator, MultiSelect, Paper, ScrollArea, Select, SimpleGrid, Space, Stack, Text, Textarea, TextInput, ThemeIcon, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { DatePickerInput } from '@mantine/dates';
import { Dropzone, FileRejection, FileWithPath } from "@mantine/dropzone";
import { useMediaQuery } from "@mantine/hooks";
import { IconMapPin, IconTag, IconUser, IconCheck, IconChevronLeft, IconPhoto, IconUpload, IconX, IconArrowBackUp, IconAlertTriangle, IconLock, IconArchive } from "@tabler/icons-react";
import moment from "moment";

import { MutableWorkOrderDto, WorkOrderDto, WorkOrderStatus } from "../../models/workorder.models";
import { useGetLocationsQuery } from "../../api/location.api";
import { useGetCategoriesQuery } from "../../api/category.api";
import { useGetUsersQuery } from "../../api/user.api";
import { useGetTeamsQuery } from "../../api/team.api";
import { useGetAssetsQuery } from "../../api/asset.api";
import { useGetVendorsQuery } from "../../api/vendor.api";
import { useWorkOrderPermissions } from "../../functions/workorder.functions";
import { PriorityInput } from "./PriorityInput";
import classes from './WorkOrderDetailView.module.css';

interface WorkOrderEditViewProps {
	workOrder: WorkOrderDto|undefined;
	isCopy: boolean;
	isRequest?: boolean;
	onSave?: (workOrder: MutableWorkOrderDto, files: File[]) => void;
	isSaving?: boolean;
	onClose?: () => void;
	onChanged?: (changed: boolean) => void;
}

export const WorkOrderEditView = ({ workOrder, isCopy, isRequest, onClose, onSave, onChanged, isSaving }: WorkOrderEditViewProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);

	const { data: locations } = useGetLocationsQuery();
	const { data: categories } = useGetCategoriesQuery();
	const { data: users } = useGetUsersQuery();
	const { data: teams } = useGetTeamsQuery();
	const { data: assets } = useGetAssetsQuery();
	const { data: vendors } = useGetVendorsQuery();

	const locationItems = locations?.map(location => ({ value: location.LocationId, label: location.Title })) || [];
	const categoryItems = categories?.map(category => ({ value: category.CategoryId, label: category.Title })) || [];
	const assetItems = assets?.map(asset => ({ value: asset.AssetId, label: `${asset.Name}${asset.AssetTag ? ` (${asset.AssetTag})` : ''}` })) || [];
	const vendorItems = vendors?.map(vendor => ({ value: vendor.VendorId, label: vendor.Name })) || [];
	const userItems = users?.filter(user => user.Deleted == false).map(user => ({ value: user.UserId, label: user.Name })) || [];
	const teamItems = teams?.filter(team => team.Deleted == false).map(team => ({ value: team.TeamId, label: team.Name })) || [];

	const [uploadFiles, setUploadFiles] = useState<FileWithPath[]>([]);
	const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
	const [rejectedFiles, setRejectedFiles] = useState<{ name: string; reason: string }[]>([]);
	const [totalSizeError, setTotalSizeError] = useState<string>('');
	const MAX_TOTAL_UPLOAD_SIZE_BYTES = 49 * 1024 * 1024;
	const isNew = !workOrder || workOrder.WorkOrderId === "new" || isCopy;
	const isArchived = workOrder?.Status === WorkOrderStatus.Archived;
	const { canCreate, canUpdate } = useWorkOrderPermissions(workOrder, teams);

	const assignmentItems: ComboboxItemGroup[] = [
		{ group: "Users", items: userItems },
		{ group: "Teams", items: teamItems },
	];

	const form = useForm<MutableWorkOrderDto>({
		initialValues: {
			WorkOrderId: workOrder?.WorkOrderId ?? (isRequest ? 'new-request' : 'new'),
			Title: workOrder?.Title || '',
			Description: workOrder?.Description || '',
			Priority: workOrder?.Priority.PriorityId || '',
			Status: workOrder?.Status || isRequest ? WorkOrderStatus.Requested : WorkOrderStatus.Open,
			StartedDate: workOrder?.StartedDate ? new Date(workOrder.StartedDate) : undefined,
			DueDate: workOrder?.DueDate ? new Date(workOrder.DueDate) : undefined,
			Location: workOrder?.Location.LocationId || '',
			Categories: workOrder?.Categories.map(category => category.CategoryId) || [],
			Assets: workOrder?.Assets.map(asset => asset.AssetId) || [],
			Vendors: workOrder?.Vendors.map(vendor => vendor.VendorId) || [],
			AssignedTo: [...workOrder?.AssignedTo?.Users.map(user => user.UserId) || [],
				...workOrder?.AssignedTo?.Teams.map(team => team.TeamId) || []
			],
			FilesToDelete: []
		},
		validate: {
			Title: (value) => (value.length < 3 ? 'Title must be at least 3 characters long' : null),
			// Description: (value) => (value && value.length < 10 ? 'Description must be at least 10 characters long' : null),
			Priority: (value) => (!isRequest && !value ? 'Priority is required' : null),
			Location: (value) => (!value ? 'Location is required' : null),
			Categories: (value) => (!isRequest && value.length === 0 ? 'At least one category is required' : null),
			AssignedTo: (value) => (!isRequest && value && value.length === 0 ? 'At least one user or team must be assigned' : null)
		}
	});

	const isChanged = isCopy || form.isDirty() || uploadFiles.length > 0 || filesToDelete.length > 0;

	const handleRemoveNewFile = (index: number) => {
		setUploadFiles((files) => files.filter((_, i) => i !== index));
	};

	const handleRemoveUploadedFile = (fileId: string) => {
		if (workOrder && onSave) {
			setFilesToDelete((files) => [...files, fileId]);
		}
	};

	const handleUndoRemoveUploadedFile = (fileId: string) => {
		if (workOrder && onSave) {
			setFilesToDelete((files) => files.filter((id) => id !== fileId));
		}
	};

	const handleRejectFiles = (files: FileRejection[]) => {
		const rejectedWithReasons = files.map(file => ({
			name: file.file.name,
			reason: file.errors?.[0]?.code === 'file-too-large' 
				? `exceeds maximum size (${(file.file.size / 1024).toFixed(2)} KB, max 10 MB)`
				: 'invalid file type'
		}));
		setRejectedFiles(rejectedWithReasons);
	};

	const validateTotalSize = (files: FileWithPath[]) => {
		const totalSize = files.reduce((sum, file) => sum + file.size, 0);
		
		if (totalSize > MAX_TOTAL_UPLOAD_SIZE_BYTES) {
			const sizeMB = (totalSize / (1024 * 1024)).toFixed(2);
			setTotalSizeError(`Total file size (${sizeMB} MB) exceeds maximum limit of 50 MB`);
			return false;
		}
		setTotalSizeError('');
		return true;
	};

	useEffect(() => {
		form.reset();
		form.setValues({
			// ...form.values,
			WorkOrderId: workOrder?.WorkOrderId ?? (isRequest ? 'new-request' : 'new')
		});
		// form.resetDirty();
	}, [isRequest]);

	useEffect(() => {
		form.setValues({
			...form.values,
			FilesToDelete: filesToDelete
		});
	}, [filesToDelete]);

	useEffect(() => {
		if (onChanged && canUpdate) {
			onChanged(isChanged);
		}
	}, [form.isDirty(), uploadFiles, onChanged]);

	useEffect(() => {
		validateTotalSize(uploadFiles);
	}, [uploadFiles]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		if (onSave) onSave(values, uploadFiles);
	};

	const handleClose = () => {
		if (onClose) onClose();
	};

	if (!canUpdate && isArchived) return (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 193px)' }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconArchive size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">
					Archived
				</Text>
				<Text ta="center">This work order has been archived and cannot be edited.</Text>
			</Stack>
		</Paper>
	);

	if ((!canCreate || (!canUpdate && !isNew)) && !isRequest) return (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 193px)' }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconLock size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">
					No Access
				</Text>
				<Text ta="center">You do not have permission to create or edit work orders. Please contact your administrator.</Text>
			</Stack>
		</Paper>
	);

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 195px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3} lineClamp={3} style={{ textAlign: isMobile ? 'center' : 'left' }}>{isNew ? 'New' : 'Edit'}{isRequest ? ' Request' : ' Work Order'}</Title>
								{workOrder?.Updated &&
								<Text fz="xs" c="dimmed" lineClamp={1} visibleFrom="sm">Last Changed on {moment(workOrder?.Updated).format("MMMM D, YYYY")}</Text>
								}
							</Stack>
						</Group>

						<Button onClick={() => handleSave(form.values)} disabled={!isChanged} loading={isSaving} variant="filled" radius="xl" miw={100} leftSection={<IconCheck />}>Save</Button>
					</Group>
				</Stack>

				<Container px="20">
					{form.errors && Object.keys(form.errors).length > 0 && <>
						<Alert title="Please fix the following errors:" color="red" mb="md" radius="lg">
							<Stack gap={0} mb={0}>
								{Object.entries(form.errors).map(([field, error]) => error && 
									<Group gap={5} key={field}>
										<ThemeIcon c="red" w={20} variant="transparent" m={-5}><IconAlertTriangle size={14} /></ThemeIcon>
										<Text key={field} fz="sm">{error}</Text>
									</Group>
								)}
							</Stack>
						</Alert>
						<Divider mb="md" />
					</>}
					<form onSubmit={form.onSubmit((values) => handleSave(values))}>
						<Button type="submit" style={{ display: 'none' }} />
						<TextInput label="Title" 
							key={form.key('Title')}
							{...form.getInputProps('Title')}
							placeholder={isRequest ? "Enter request title" : "Enter work order title"}
							maxLength={255}
							required 
							rightSection={isNew ?
								<ActionIcon variant="light" radius="sm" size="md" onClick={() => { form.setFieldValue('Title', ''); }}>
									<IconX size={14} />
								</ActionIcon> : null
							}
							mb="xl" />

						<Textarea label="Description"
							key={form.key('Description')}
							{...form.getInputProps('Description')}
							placeholder={isRequest ? "Enter request description" : "Enter work order description"}
							mb="xl"
							autosize
							minRows={3}
							rightSection={isNew ?
								<ActionIcon variant="light" radius="sm" size="md" style={{ alignSelf: 'flex-start', marginTop: '4px' }} onClick={() => { form.setFieldValue('Description', ''); }}>
									<IconX size={14} />
								</ActionIcon> : null
							}
						/>

						<Stack gap="xs" mb="xl">
							<Text fz="sm" fw="bold">Pictures</Text>
							<SimpleGrid cols={(workOrder?.Files.length ?? 0) + uploadFiles.length > 0 ? 3 : 1} spacing="xs">
								{workOrder?.Files.filter(file => file.IsImage).map((file, index) => (
									<React.Fragment key={file.FileId}>
										{filesToDelete.includes(file.FileId) ? 
											<Indicator label={<Tooltip label="Undo Remove"><ActionIcon onClick={() => handleUndoRemoveUploadedFile(file.FileId) } color="dimmed" radius="xl" size="sm"><IconArrowBackUp /></ActionIcon></Tooltip>} size={16} color="transparent" offset={15} position="top-end">
												<Image src={file.Url} alt={file.Name} radius="md" style={{ objectFit: 'cover', aspectRatio: '4/3', width: '100%', height: 'auto', filter: 'opacity(0.25)' }} />
											</Indicator> :												
											<Indicator label={<Tooltip label="Remove"><ActionIcon onClick={() => handleRemoveUploadedFile(file.FileId) } color="red" radius="xl" size="sm"><IconX /></ActionIcon></Tooltip>} size={16} color="transparent" offset={15} position="top-end">
												<Image src={file.Url} alt={file.Name} radius="md" style={{ objectFit: 'cover', aspectRatio: '4/3', width: '100%', height: 'auto' }} />
											</Indicator>
										}
									</React.Fragment>
								))}
								{ uploadFiles.map((file, index) => <React.Fragment key={index}>
									<Indicator label={<Tooltip label="Remove"><ActionIcon onClick={(e) => { e.preventDefault(); handleRemoveNewFile(index); }} color="red" radius="xl" size="sm"><IconX /></ActionIcon></Tooltip>} size={16} color="transparent" offset={15} position="top-end">
										<Image src={URL.createObjectURL(file)} alt={file.name} radius="md" style={{ objectFit: 'cover', aspectRatio: '4/3', width: '100%', height: 'auto' }} />
									</Indicator>
								</React.Fragment>)}
								<Dropzone
									onDrop={(files) => {
										setUploadFiles((prev) => {
											const newFiles = [...prev, ...files];
											if (!validateTotalSize(newFiles)) return prev;
											setRejectedFiles([]);
											return newFiles;
										});
									}}
									onReject={(files) => handleRejectFiles(files)}
									accept={['image/png', 'image/jpeg']}
									// maxSize={10 * 1024 * 1024} // 10 MB
									// maxFiles={10}
									radius="md"
									mih={120}
									>
									<Stack justify="center" align="center" gap="md"  style={{ pointerEvents: 'none' }}>
										<Dropzone.Accept>
											<IconUpload size={25} color="var(--mantine-color-blue-6)" stroke={1.5} />
										</Dropzone.Accept>
										<Dropzone.Reject>
											<IconX size={25} color="var(--mantine-color-red-6)" stroke={1.5} />
										</Dropzone.Reject>
										<Dropzone.Idle>
											<IconPhoto size={25} color="var(--mantine-color-dimmed)" stroke={1.5} />
										</Dropzone.Idle>

										<Text size="sm" inline style={{ textAlign: 'center' }}>
											Drag images here or click to select files
										</Text>
									</Stack>
								</Dropzone>
								{rejectedFiles.length > 0 && (
									<Alert title="Upload Error" color="red" mt="md" icon={<IconAlertTriangle size={16} />}>
										<Stack gap={4}>
											{rejectedFiles.map((file, index) => (
												<Text key={index} fz="sm">{file.name}: {file.reason}</Text>
											))}
										</Stack>
									</Alert>
								)}

							</SimpleGrid>
							{totalSizeError !== "" && (
								<Alert title="Upload Error" color="red" mt="md" icon={<IconAlertTriangle size={16} />}>
									<Text fz="sm">{totalSizeError}</Text>
								</Alert>
							)}
						</Stack>


						<PriorityInput
							label="Priority"
							value={form.values.Priority}
							hidden={isRequest}
							onChange={(value) => form.setFieldValue('Priority', value)}
							error={form.errors.Priority}
						/>

						<Space h="lg" />

						{!isRequest && <>
						<SimpleGrid cols={isMobile ? 1 : 2} spacing="xl" mb="xl">
							<DatePickerInput
								label="Start Date"
								key={form.key('StartedDate')}
								{...form.getInputProps('StartedDate')}
								placeholder="Select start date"
								minDate={workOrder?.Created ? new Date(workOrder.Created) : undefined}
								clearable
							/>
							<DatePickerInput
								label="Due Date"
								key={form.key('DueDate')}
								{...form.getInputProps('DueDate')}
								placeholder="Select due date"
								minDate={workOrder?.Created ? new Date(workOrder.Created) : undefined}
								clearable
							/>
						</SimpleGrid>
						</>}

						{!isRequest &&
							<MultiSelect label="Assigned To"
								key={form.key('AssignedTo')}
								data={assignmentItems}
								{...form.getInputProps('AssignedTo')}
								placeholder="Select users or teams"
								checkIconPosition="right"
								required
								searchable
								leftSection={<IconUser size={16} />}
								mb="xl"
							/>
						}

						<Select label="Location"
							key={form.key('Location')}
							{...form.getInputProps('Location')}
							data={locationItems}
							placeholder="Select a location"
							checkIconPosition="left"
							required
							searchable
							leftSection={<IconMapPin size={16} />}
							mb="xl"
						/>

						{!isRequest &&
							<MultiSelect label="Categories"
								key={form.key('Categories')}
								{...form.getInputProps('Categories')}
								data={categoryItems}
								placeholder="Select categories"
								checkIconPosition="left"
								required
								searchable
								leftSection={<IconTag size={16} />}
								mb="xl"
							/>
						}

						{!isRequest &&
							<MultiSelect label="Assets"
								key={form.key('Assets')}
								{...form.getInputProps('Assets')}
								data={assetItems}
								placeholder="Select assets"
								checkIconPosition="left"
								searchable
								mb="xl"
							/>
						}

						{!isRequest &&
							<MultiSelect label="Vendors"
								key={form.key('Vendors')}
								{...form.getInputProps('Vendors')}
								data={vendorItems}
								placeholder="Select vendors"
								checkIconPosition="left"
								searchable
								mb="xl"
							/>
						}
					</form>
					{/* <pre>
						<small>{JSON.stringify(form.values, null, 2)}</small>
					</pre> */}
				</Container>
			</ScrollArea>
		</Paper>
	</>;
};

